import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { inputCls, Field, Panel, ActiveBadge, FormActions, errMsg } from "./ui";
import DeleteButton from "./DeleteButton";
import PaymentTimeoutPanel from "./PaymentTimeoutPanel";
import PayPalPanel from "./PayPalSettings";
import BinancePaySettings from "./BinancePaySettings";
import ImageUpload from "./ImageUpload";
import jsQR from "jsqr";
import { QRCodeSVG } from "qrcode.react";

const EMPTY = { name: "", type: "bank", details: "", active: true, currency: "IDR", qris_payload: "", network: "TRC20", account_name: "", qr_image: "" };
const TYPE_LABEL = { bank: "Transfer Bank", ewallet: "E-Wallet", qris: "QRIS (nominal otomatis)", crypto: "Binance USDT (wallet)", binance_pay: "Binance Pay (QR + UID)" };
const USDT_TYPES = ["crypto", "binance_pay"];
const detailLabel = (f) => ({ qris: "Nama merchant QRIS", crypto: `Alamat wallet USDT ${f.network}`, binance_pay: "Binance Pay UID" }[f.type] || "Detail (no. rekening / alamat)");
const detailPh = (f) => ({ qris: "MQL STORE", crypto: f.network === "BEP20" ? "0x... (42 karakter)" : "T... (34 karakter)", binance_pay: "mis. 123456789" }[f.type] || "1234567890 a.n. MQL Group");

function UsdtFields({ form, setForm }) {
  if (form.type === "crypto") return (
    <Field label="Jaringan USDT">
      <select data-testid="pm-network-select" className={inputCls} value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })}>
        <option value="TRC20">TRC20 (Tron)</option>
        <option value="BEP20">BEP20 (BNB Smart Chain)</option>
      </select>
    </Field>
  );
  if (form.type !== "binance_pay") return null;
  return (
    <>
      <Field label="Nama akun Binance"><input data-testid="pm-account-name-input" maxLength={100} placeholder="mis. MQL Group" className={inputCls} value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} /></Field>
      <div className="md:col-span-2"><ImageUpload value={form.qr_image} onChange={(v) => setForm({ ...form, qr_image: v })} testPrefix="pm-binance-qr" label="QR Code Binance Pay *" /></div>
    </>
  );
}

const decodeQr = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    const s = Math.min(1, 1600 / Math.max(img.width, img.height));
    c.width = img.width * s; c.height = img.height * s;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const r = jsQR(ctx.getImageData(0, 0, c.width, c.height).data, c.width, c.height);
    URL.revokeObjectURL(img.src);
    resolve(r?.data || null);
  };
  img.onerror = () => resolve(null);
  img.src = URL.createObjectURL(file);
});

function QrisUpload({ value, onChange }) {
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const data = await decodeQr(f);
    if (!data || !data.startsWith("000201")) { toast.error("QR tidak terbaca atau bukan QRIS. Coba gambar yang lebih jelas."); return; }
    onChange(data); toast.success("QRIS terbaca");
  };
  return (
    <div className="md:col-span-3 flex items-center gap-4 bg-[#191A1F] border border-[#35353B] rounded-sm p-3">
      {value ? <div className="bg-white p-2 rounded"><QRCodeSVG data-testid="pm-qris-preview" value={value} size={88} /></div> : <div className="w-[104px] h-[104px] border border-dashed border-[#35353B] rounded flex items-center justify-center text-[10px] text-[#A8A8AD] text-center">Belum ada QRIS</div>}
      <div className="text-xs text-[#A8A8AD] space-y-2">
        <p>Unggah gambar/screenshot QRIS statis toko Anda. Nominal + kode unik akan otomatis dimasukkan ke QR untuk setiap pesanan.</p>
        <input data-testid="pm-qris-file-input" type="file" accept="image/*" onChange={pick} className="text-xs" />
      </div>
    </div>
  );
}

export default function PaymentsTab() {
  const [pms, setPms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/payment-methods").then((r) => setPms(r.data));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.patch(`/payment-methods/${editId}`, payload());
      else await api.post("/payment-methods", payload());
      toast.success(editId ? "Metode diperbarui" : "Metode ditambahkan");
      reset(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const edit = (m) => { setEditId(m.id); setForm({ name: m.name, type: m.type, details: m.details, active: m.active, currency: m.currency || "IDR", qris_payload: m.qris_payload || "", network: m.network || "TRC20", account_name: m.account_name || "", qr_image: m.qr_image || "" }); };
  const payload = () => ({ ...form, qris_payload: form.qris_payload || null, qr_image: form.qr_image || null, account_name: form.account_name || null });
  const [toggling, setToggling] = useState("");
  const toggleActive = async (m) => {
    setToggling(m.id);
    try {
      await api.patch(`/payment-methods/${m.id}`, { active: !m.active });
      toast.success(`${m.name} ${m.active ? "dinonaktifkan — disembunyikan dari checkout" : "diaktifkan — tampil di checkout"}`);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setToggling(""); }
  };

  return (
    <>
      <PaymentTimeoutPanel />
      <PayPalPanel />
      <BinancePaySettings />
      <Panel title={editId ? "Ubah Metode Pembayaran" : "Tambah Metode Pembayaran"} testId="pm-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nama"><input data-testid="pm-name-input" required placeholder="mis. BCA" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Jenis">
            <select data-testid="pm-type-select" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label={detailLabel(form)}><input data-testid="pm-details-input" required placeholder={detailPh(form)} className={inputCls} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></Field>
          {form.type === "qris" && <QrisUpload value={form.qris_payload} onChange={(v) => setForm({ ...form, qris_payload: v })} />}
          <UsdtFields form={form} setForm={setForm} />
          <Field label="Mata uang pembayaran">
            <select data-testid="pm-currency-select" disabled={form.type === "qris" || USDT_TYPES.includes(form.type)} className={inputCls} value={form.type === "qris" ? "IDR" : USDT_TYPES.includes(form.type) ? "USDT" : form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="IDR">IDR (Rupiah)</option>
              <option value="USD">USD ($) — kurs otomatis</option>
              {USDT_TYPES.includes(form.type) && <option value="USDT">USDT — konversi otomatis dari IDR</option>}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#F5F3ED]">
            <input data-testid="pm-active-checkbox" type="checkbox" className="accent-[#D4AF37]" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif (tampil saat checkout)
          </label>
          <div className="md:col-span-2 flex md:justify-end"><FormActions editing={!!editId} onCancel={reset} saving={saving} testPrefix="pm" /></div>
        </form>
      </Panel>

      <Panel title="Daftar Metode Pembayaran" testId="pm-list-panel">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]">
            <th className="py-2">Nama</th><th className="py-2">Jenis</th><th className="py-2">Detail</th><th className="py-2">Status</th><th className="py-2 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {pms.map((m) => (
              <tr key={m.id} data-testid={`pm-row-${m.id}`} className="border-b border-[#35353B]">
                <td className="py-3 text-[#F5F3ED]">{m.name}</td>
                <td className="py-3 text-[#A8A8AD]">{TYPE_LABEL[m.type] || m.type} · {USDT_TYPES.includes(m.type) ? `USDT${m.type === "crypto" ? ` ${m.network || "TRC20"}` : ""}` : m.currency || "IDR"}</td>
                <td className="py-3 font-mono text-xs text-[#F5F3ED]">{m.type === "binance_pay" ? `UID ${m.details}${m.account_name ? ` · ${m.account_name}` : ""}` : m.details}</td>
                <td className="py-3">
                  <button data-testid={`pm-toggle-${m.id}`} role="switch" aria-checked={m.active} disabled={toggling === m.id} onClick={() => toggleActive(m)}
                    title={m.active ? "Klik untuk nonaktifkan" : "Klik untuk aktifkan"} className="inline-flex items-center gap-2 disabled:opacity-50">
                    <span className={`relative w-9 h-5 rounded-full transition-colors ${m.active ? "bg-[#D4AF37]" : "bg-[#35353B]"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#101114] transition-transform ${m.active ? "translate-x-4" : ""}`} />
                    </span>
                    <ActiveBadge active={m.active} />
                  </button>
                </td>
                <td className="py-3 text-right"><button data-testid={`edit-pm-${m.id}`} onClick={() => edit(m)} className="text-[#D4AF37] hover:underline text-xs">Ubah</button>
                  <DeleteButton endpoint={`/payment-methods/${m.id}`} name={m.name} testId={`delete-pm-${m.id}`}
                    onDone={() => { if (editId === m.id) reset(); load(); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
